import { useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, Plus } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { uploadDocumentFile, useUpdateDocument, useUpdateProfile } from "@/hooks/useUpdateProfile";
import type ReactSignatureCanvas from "react-signature-canvas";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Document, Page, pdfjs } from 'react-pdf';
import { useDocuments, useProfile } from "@/hooks/useProfile";
import { ENV } from "@/constant";
import { ImageWithLoader } from "@/components/ui/image-with-loader";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import dummyDriverLicenceImage from "../../images/dummydriverlicense.jpg";
import { useModal } from "@/components/ui/ModalProvider";
import { client } from "@/utils/apiClients";
import { useTheme } from "@/hooks/useTheme";

// Set the workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const CUSTOM_BRAND_OBJECT_ID = "69e7633d291f1a045c1a8921";

const RenderForm = () => {
    // const { formHeader } = useParams();
    const location = useLocation();
    // const navigate = useNavigate();
    const { theme } = useTheme();
    const formData = location.state?.formData;
    const fromPath = location.state?.from as string | undefined;
    const decodedHeader = decodeURIComponent(formData?.header || "");
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const navigate = useNavigate();
    const { updateDocument } = useUpdateDocument();
    const { uploadFile } = uploadDocumentFile();
    const { showModal } = useModal();
    const { mutate: refreshProfile } = useProfile();
    // Avoid shadowing the global `document` object by renaming the response field
    const { document: documentsData, mutate: refreshDocuments } = useDocuments();

    const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
    const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
    const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>({});
    const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB

    const [vehicleBrands, setVehicleBrands] = useState<any[]>([]);
    const [vehicleModels, setVehicleModels] = useState<any[]>([]);
    const [modelYears, setModelYears] = useState<any[]>([]);
    const [modelColors, setModelColors] = useState<any[]>([]);
    const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
    const [useCustomVehicleMake, setUseCustomVehicleMake] = useState(false);
    const [useCustomVehicleModel, setUseCustomVehicleModel] = useState(false);
    const [useCustomVehicleYear, setUseCustomVehicleYear] = useState(false);
    const [useCustomVehicleColor, setUseCustomVehicleColor] = useState(false);

    const [loadingBrands, setLoadingBrands] = useState(false);
    const [loadingModels, setLoadingModels] = useState(false);
    const [loadingYears, setLoadingYears] = useState(false);
    const [isFetchingVin, setIsFetchingVin] = useState(false);
    const [isDecodingVin, setIsDecodingVin] = useState(false);
    const [isManualVin, setIsManualVin] = useState(false);
    const [autoFilledVin, setAutoFilledVin] = useState("");

    const extractVinNo = (res: any): string => {
        const direct =
            res?.vin ??
            res?.vinNo ??
            res?.serialNo ??
            res?.data?.vin ??
            res?.data?.vinNo ??
            res?.data?.serialNo;

        if (typeof direct === "string" && direct.trim()) {
            return direct.trim();
        }

        if (Array.isArray(res?.data) && res.data.length > 0) {
            const first = res.data[0];
            const fromArray = first?.vin ?? first?.vinNo ?? first?.serialNo;
            if (typeof fromArray === "string" && fromArray.trim()) {
                return fromArray.trim();
            }
        }

        return "";
    };

    const extractDecodedVehicleInfo = (res: any): { make: string; model: string; year: string } => {
        const source = Array.isArray(res?.data)
            ? (res.data[0] ?? {})
            : (res?.data ?? res ?? {});
        const vinDecoded = source?.vinDecodedData ?? source;
        const specs = vinDecoded?.specifications ?? {};

        const make = String(
            specs?.make ??
            vinDecoded?.make ??
            source?.vehicleMake ??
            source?.make ??
            source?.manufacturer ??
            ""
        ).trim();
        const model = String(
            specs?.model ??
            vinDecoded?.model ??
            source?.vehicleModel ??
            source?.model ??
            source?.modelName ??
            ""
        ).trim();
        const year = String(
            vinDecoded?.year ?? source?.vehicleYear ?? source?.year ?? ""
        ).trim();

        return { make, model, year };
    };

    const fetchVehicleInfoByVin = async (vin: string) => {
        if (!vin?.trim()) return { make: "", model: "", year: "" };

        try {
            setIsDecodingVin(true);
            const res: any = await client("/profile/vehicles/vinDecoder2", {
                body: { vin: vin.trim() },
            });
            return extractDecodedVehicleInfo(res);
        } catch (error) {
            console.error("Error decoding VIN:", error);
            return { make: "", model: "", year: "" };
        } finally {
            setIsDecodingVin(false);
        }
    };

    const fetchVinByPlateNo = async (plateNo: string): Promise<string> => {
        if (!plateNo?.trim()) return "";

        try {
            setIsFetchingVin(true);
            const res: any = await client(
                "/profile/vehicles/plateToVin",
                {
                    body: {
                        plateNumber: plateNo.trim(),
                        country: "CA",
                    },
                }
            );
            return extractVinNo(res);
        } catch (error) {
            console.error("Error fetching VIN by plate number:", error);
            return "";
        } finally {
            setIsFetchingVin(false);
        }
    };

    async function compressImage(file: File, maxBytes: number): Promise<File> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(url);

                let width = img.naturalWidth || img.width;
                let height = img.naturalHeight || img.height;

                // Calculate new dimensions while maintaining aspect ratio
                const maxDimension = 2048; // Maximum dimension for very large images
                if (width > maxDimension || height > maxDimension) {
                    const scale = maxDimension / Math.max(width, height);
                    width = Math.floor(width * scale);
                    height = Math.floor(height * scale);
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                if (!ctx) {
                    reject(new Error("Could not create canvas context"));
                    return;
                }

                // Draw image on canvas
                ctx.drawImage(img, 0, 0, width, height);

                // Try different quality levels
                let quality = 0.9;
                let attempts = 0;
                const minQuality = 0.4;

                const tryCompress = () => {
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error("Failed to create blob"));
                            return;
                        }

                        if (blob.size <= maxBytes || quality <= minQuality || attempts >= 5) {
                            const compressedFile = new File([blob], file.name, {
                                type: "image/jpeg",
                                lastModified: Date.now(),
                            });
                            resolve(compressedFile);
                        } else {
                            quality -= 0.1;
                            attempts++;
                            setTimeout(tryCompress, 0);
                        }
                    }, "image/jpeg", quality);
                };

                tryCompress();
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error("Failed to load image"));
            };

            img.src = url;
        });
    }

    const currentDoc = documentsData?.documents?.find(
        (doc: any) => doc.docType?.toLowerCase() === decodedHeader.toLowerCase()
    );

    // Scroll to top whenever the form is opened or changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [decodedHeader]);

    useEffect(() => {
        const hasSelectAndInput = formData?.fields?.some(
            (field: any) => field.type?.toLowerCase() === "selectandinput"
        );
        if (!hasSelectAndInput) return;

        fetchBrands();
    }, [formData]);

    // When coming in with an already selected vehicle make/model/year,
    // pre-populate models, years, and colors by calling the relevant APIs.
    useEffect(() => {
        const hasSelectAndInput = formData?.fields?.some(
            (field: any) => field.type?.toLowerCase() === "selectandinput"
        );
        if (!hasSelectAndInput) return;
        if (!currentDoc?.data) return;
        if (!vehicleBrands.length) return; // wait until brands are loaded

        const savedMake = currentDoc.data["vehicleMake"];
        const savedModel = currentDoc.data["vehicleModel"];
        const savedYear = currentDoc.data["vehicleYear"];

        if (!savedMake) return;

        const matchedBrand = vehicleBrands.find((b) => getBrandLabel(b) === savedMake);
        if (!matchedBrand) return;

        const brandId = matchedBrand?._id ?? matchedBrand?.id ?? matchedBrand?.value;
        if (!brandId) return;

        (async () => {
            try {
                const brandIdStr = String(brandId);

                const modelKeyForYears = savedModel ? String(savedModel) : "";

                const [models, years] = await Promise.all([
                    fetchModelsForBrand(brandIdStr),
                    modelKeyForYears
                        ? fetchYearsForModel(brandIdStr, modelKeyForYears)
                        : Promise.resolve([] as any[]),
                ]);

                // If we have a saved year, find its colors and pre-populate modelColors
                if (savedYear && Array.isArray(years) && years.length > 0) {
                    const savedYearStr = String(savedYear);
                    const matchedYearObj = years.find((y: any) => {
                        if (typeof y === "object" && y !== null && "year" in y) {
                            return String(y.year) === savedYearStr;
                        }
                        const label = getYearLabel(y);
                        return String(label) === savedYearStr;
                    });

                    if (matchedYearObj && Array.isArray(matchedYearObj.colors)) {
                        setModelColors(matchedYearObj.colors);
                    }
                }
            } catch (error) {
                console.error("Error preloading vehicle select data:", error);
            }
        })();
    }, [formData, currentDoc, vehicleBrands]);
    const fetchBrands = async () => {
        try {
            setLoadingBrands(true);
            const res: any = await client("/vehicle-data/brands", {});
            const list = res?.data || [];
            setVehicleBrands(list);
            return list as any[];

        } catch (error) {
            console.error("Error fetching vehicle brands:", error);
            return [] as any[];
        } finally {
            setLoadingBrands(false);
        }
    };

    if (!formData) {
        return <div className="p-4">Form data not found.</div>;
    }

    // dynamic validation schema
    const validationSchemaShape: any = {};
    formData?.fields?.forEach((field: any) => {
        if (field.required) {

            switch (field.type.toLowerCase()) {

                case "text": {
                    const keyLower = typeof field.key === "string" ? field.key.toLowerCase() : "";

                    // GST-specific validation for text fields whose key contains "gst"
                    if (keyLower.includes("gst")) {
                        validationSchemaShape[field.key] = Yup.string()
                            .transform((value) => (typeof value === "string" ? value.trim().toUpperCase() : value))
                            .matches(/^[0-9A-Z]{15}$/, `${field.label || field.key} must be a valid GST number (15 characters).`)
                            .required(`${field.label || field.key} is required`);
                    } else if (keyLower.includes("sin")) {
                        validationSchemaShape[field.key] = Yup.string()
                            .transform((value) => (typeof value === "string" ? value.trim().toUpperCase() : value))
                            .matches(/^[0-9]{9}$/, `${field.label || field.key} must be a valid SIN number`)
                            .required(`${field.label || field.key} is required`);
                    }
                    else {
                        validationSchemaShape[field.key] = Yup.string()
                            .required(`${field.label || field.key} is required`);
                    }
                    break;
                }
                case "number":
                    validationSchemaShape[field.key] = Yup.number()
                        .typeError(`${field.label || field.key} must be a number`)
                        .required(`${field.label || field.key} is required`);
                    break;
                case "date":
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    if (decodedHeader.toLowerCase() === "vehicle inspection") {
                        validationSchemaShape[field.key] = Yup.date()
                            .transform((value, originalValue) => {

                                if (originalValue) {
                                    const date = new Date(originalValue);
                                    date.setHours(0, 0, 0, 0);
                                    return date;
                                }
                                return value;
                            })
                            .max(today, `${field.label || field.key} cannot be a future date`)
                            .required(`${field.label || field.key} is required`);
                    }
                    else if (decodedHeader.toLowerCase() === "proof of work eligibility") {
                        validationSchemaShape[field.key] = Yup.date()
                            .required(`${field.label || field.key} is required`);
                    }
                    else {
                        validationSchemaShape[field.key] = Yup.date()
                            .min(today, `${field.label || field.key} must be today or a future date`)
                            .required(`${field.label || field.key} is required`);
                    }
                    break;
                case "file":
                    validationSchemaShape[field.key] = Yup.mixed()
                        .required(`Please upload ${field.label || field.key}`);
                    break;
                case "checkbox":
                    validationSchemaShape[field.key] = Yup.boolean()
                        .oneOf([true], `You must accept ${field.label || field.key}`)
                        .required(`${field.label || field.key} is required`);
                    break;
                case "signature":
                    validationSchemaShape[field.key] = Yup.string().required("Signature is required");
                    break;
                default:
                    validationSchemaShape[field.key] = Yup.string()
                        .required(`${field.label || field.key} is required`);
            }
        }
    });

    function base64ToBlob(base64: string) {
        const parts = base64.split(",");
        const header = parts[0] ?? "";
        const data = parts[1] ?? "";

        const match = header.match(/:(.*?);/);
        if (!match || !match[1] || !data) {
            throw new Error("Invalid base64 string");
        }
        const mime = match[1];

        let bstr: string;
        try {
            bstr = atob(data);
        } catch {
            throw new Error("Invalid base64 data");
        }

        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) {
            u8arr[i] = bstr.charCodeAt(i);
        }
        const blob = new Blob([u8arr], { type: mime });

        const file = new File([blob], "upload.jpg", { type: mime });

        return file;
    }

    const validationSchema = Yup.object().shape(validationSchemaShape);

    // Default initial values
    const initialValues: any = {};
    formData?.fields?.forEach((field: any) => {
        const type = field.type.toLowerCase();
        if (currentDoc?.data && currentDoc.data[field.key] !== undefined && currentDoc.data[field.key] !== null) {
            initialValues[field.key] = currentDoc.data[field.key];
        } else if (type === "file" || type === "signature") {
            initialValues[field.key] = null;
        } else if (type === "checkbox") {
            initialValues[field.key] = false;
        } else {
            initialValues[field.key] = "";
        }
    });

    const hasSerialNoField = formData?.fields?.some((field: any) => field.key === "serialNo");


    const handleBack = () => {
        // navigate(fromPath || '/documents', { replace: true });
        navigate(-1);
    };
    const isSubmittingRef = useRef(false);

    const getBrandLabel = (brand: any) => brand?.label ?? brand?.name ?? brand?.title ?? String(brand);
    const getModelLabel = (model: any) => model?.label ?? model?.name ?? model?.modelName ?? String(model);
    // For year objects coming from API: { year: 2017, colors: [...] }
    const getYearLabel = (year: any) =>
        (typeof year === "object" && year !== null && ("year" in year))
            ? String(year.year)
            : (year?.label ?? year?.name ?? String(year));
    const getColorLabel = (color: any) => color?.label ?? color?.name ?? String(color);
    const getColorValue = (color: any) => color?.value ?? color?.code ?? color?.hex ?? color?.hexCode ?? getColorLabel(color);
    const getDisplayHexColor = (color: any): string | null => {
        const raw = color?.hexCode ?? color?.hex ?? color?.code ?? color?.value ?? null;
        if (!raw) return null;
        const normalized = String(raw).trim();
        const withHash = normalized.startsWith("#") ? normalized : `#${normalized}`;
        return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(withHash) ? withHash : null;
    };
    const normalizeText = (val: any) => String(val ?? "").trim().toLowerCase();

    const fetchModelsForBrand = async (brandId: string) => {
        try {
            setLoadingModels(true);
            const res: any = await client(`/vehicle-data/models?brandId=${brandId}`, {});
            const models = res?.data || [];
            setVehicleModels(models);
            setModelYears([]);
            setModelColors([]);
            setUseCustomVehicleColor(false);
            setUseCustomVehicleModel(false);
            setSelectedBrandId(brandId);
            return models as any[];
        } catch (error) {
            console.error("Error fetching vehicle models:", error);
            return [] as any[];
        } finally {
            setLoadingModels(false);
        }
    };

    const fetchYearsForModel = async (brandId: string, modelKey: string, yearKey?: string | number) => {
        try {
            setLoadingYears(true);
            const normalizedYearKey =
                yearKey !== undefined && yearKey !== null && String(yearKey).trim() !== ""
                    ? String(yearKey).trim()
                    : "";
            const yearQuery = yearKey !== undefined && yearKey !== null && String(yearKey).trim() !== ""
                ? `&year=${encodeURIComponent(String(yearKey).trim())}`
                : "";
            const res: any = await client(
                `/vehicle-data/models?brandId=${brandId}&modelName=${encodeURIComponent(modelKey)}${yearQuery}`,
                {}
            );

            const raw = res?.data;
            let yearsList: any[] = [];

            // New API shape: array of models, each with `years: [{ year, colors: [...] }]`
            if (Array.isArray(raw)) {
                if (raw.length > 0 && Array.isArray(raw[0]?.years)) {
                    yearsList = raw[0].years;
                } else {
                    // Fallback: treat whole array as year list
                    yearsList = raw;
                }
                setModelYears(yearsList);
                // If year is provided, prioritize that year's colors.
                if (normalizedYearKey) {
                    const matchedYearBucket = yearsList.find((y: any) =>
                        typeof y === "object" && y !== null &&
                        String(y.year ?? "").trim() === normalizedYearKey &&
                        Array.isArray(y.colors)
                    );

                    if (matchedYearBucket && Array.isArray(matchedYearBucket.colors)) {
                        setModelColors(matchedYearBucket.colors);
                    } else {
                        setModelColors([]);
                    }
                } else {
                    // Some APIs return default colors under year: null.
                    // Preload those colors so color selection is available immediately.
                    const nullYearBucket = yearsList.find((y: any) =>
                        typeof y === "object" && y !== null &&
                        (y.year === null || y.year === undefined || String(y.year).trim() === "") &&
                        Array.isArray(y.colors)
                    );
                    if (nullYearBucket && Array.isArray(nullYearBucket.colors)) {
                        setModelColors(nullYearBucket.colors);
                    } else {
                        setModelColors([]); // colors will be set when a specific year is chosen
                    }
                }
            } else if (raw) {
                // Backwards-compatibility: older shapes
                if (Array.isArray(raw.years)) {
                    yearsList = raw.years;
                    setModelYears(raw.years);
                }
                if (Array.isArray(raw.colors)) {
                    setModelColors(raw.colors);
                }
            }

            return yearsList;
        } catch (error) {
            console.error("Error fetching vehicle years/colors:", error);
            setModelYears([]);
            setModelColors([]);
            return [] as any[];
        } finally {
            setLoadingYears(false);
        }
    };

    const populateVehicleFieldsFromVin = async (vinNo: string, form: any) => {
        const decoded = await fetchVehicleInfoByVin(vinNo);
        if (!decoded.make && !decoded.model && !decoded.year) return;

        let brands = vehicleBrands;
        if (!brands.length) {
            brands = await fetchBrands();
        }

        const matchedBrand = brands.find(
            (brand) => normalizeText(getBrandLabel(brand)) === normalizeText(decoded.make)
        );

        if (!matchedBrand) {
            if (decoded.make) {
                setUseCustomVehicleMake(true);
                form.setFieldValue("vehicleMake", decoded.make);
            }
            if (decoded.model) {
                setUseCustomVehicleModel(true);
                form.setFieldValue("vehicleModel", decoded.model);
            }
            if (decoded.year) {
                form.setFieldValue("vehicleYear", decoded.year);
            }

            // If decoded make does not match, use a dedicated custom brand objectId
            // (not from brands list) so backend can return default model/year/colors.
            if (CUSTOM_BRAND_OBJECT_ID) {
                const models = await fetchModelsForBrand(String(CUSTOM_BRAND_OBJECT_ID));

                const firstModel = models[0];
                const fallbackModelKey =
                    decoded.model ||
                    (firstModel?.modelName ?? firstModel?.name ?? firstModel?.label ?? "");

                if (fallbackModelKey) {
                    await fetchYearsForModel(String(CUSTOM_BRAND_OBJECT_ID), String(fallbackModelKey));
                }
            }
            return;
        }

        const brandId = matchedBrand?._id ?? matchedBrand?.id ?? matchedBrand?.value;
        if (!brandId) return;

        const brandLabel = getBrandLabel(matchedBrand);
        form.setFieldValue("vehicleMake", brandLabel);
        setUseCustomVehicleMake(false);

        const models = await fetchModelsForBrand(String(brandId));

        let modelKeyForYearLookup = decoded.model;
        if (decoded.model) {
            const matchedModel = models.find(
                (model: any) => normalizeText(getModelLabel(model)) === normalizeText(decoded.model)
            );

            if (matchedModel) {
                setUseCustomVehicleModel(false);
                form.setFieldValue("vehicleModel", getModelLabel(matchedModel));
                modelKeyForYearLookup = String(matchedModel?.modelName ?? getModelLabel(matchedModel));
            } else {
                setUseCustomVehicleModel(true);
                form.setFieldValue("vehicleModel", decoded.model);
            }
        }

        if (!modelKeyForYearLookup) return;

        const years = await fetchYearsForModel(String(brandId), modelKeyForYearLookup, decoded.year || undefined);
        if (!decoded.year) {
            // If decoder has no year, keep default/null-year colors available.
            const nullYearBucket = years.find((y: any) =>
                typeof y === "object" && y !== null &&
                (y.year === null || y.year === undefined || String(y.year).trim() === "") &&
                Array.isArray(y.colors)
            );
            if (nullYearBucket && Array.isArray(nullYearBucket.colors)) {
                setModelColors(nullYearBucket.colors);
            }
            return;
        }

        const matchedYear = years.find((yearObj: any) => {
            if (typeof yearObj === "object" && yearObj !== null && "year" in yearObj) {
                return String(yearObj.year) === decoded.year;
            }
            return String(getYearLabel(yearObj)) === decoded.year;
        });

        form.setFieldValue(
            "vehicleYear",
            matchedYear
                ? String((matchedYear as any)?.year ?? getYearLabel(matchedYear))
                : decoded.year
        );
    };


    return (
        <div className="bg-white dark:bg-gray-900 text-black dark:text-white min-h-screen">
            {/* <div className="flex items-center justify-between w-full mb-6">
                <div onClick={handleBack} className="cursor-pointer">
                    <ChevronLeft />
                </div>
                <div className="flex-1 text-center">
                    <h1>{decodedHeader || "Documents"}</h1>
                </div>
                <div className="w-6"></div>
            </div> */}
            {
                decodedHeader !== "Community Safety Education Confirmation" &&
                <div className="flex items-center justify-between p-4 border-b bg-white dark:bg-gray-800 text-black dark:text-white border-gray-200 dark:border-gray-700" >
                    <div onClick={handleBack} className="cursor-pointer">
                        <ChevronLeft />
                    </div>
                    <div className="flex-1 text-center">
                        <h1 className='text-lg font-semibold'>{decodedHeader || "Documents"}</h1>
                    </div>
                    <div className="w-6"></div>
                </div>
            }
            <div className="p-4">

                {formData?.instructions_html && (
                    <div
                        className="text-sm text-gray-800 dark:text-gray-300 mb-6"
                        dangerouslySetInnerHTML={{ __html: formData?.instructions_html }}
                    />
                )}

                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        if (isSubmittingRef.current) return;
                        isSubmittingRef.current = true;
                        try {
                            const formDataToSend: any = {};
                            // Loop over each form field
                            for (const [key, val] of Object.entries(values)) {
                                const fieldConfig = formData?.fields?.find((f: any) => f.key === key);
                                const fieldType = fieldConfig?.type?.toLowerCase();

                                if (val instanceof File) {
                                    // Upload local file first
                                    const response = await uploadFile(val);

                                    if (response?.data?.fileLink) {
                                        // Replace local file with uploaded URL
                                        formDataToSend[key] = response.data.fileLink;
                                    } else {
                                        alert(`File upload failed for ${key}`);
                                        formDataToSend[key] = null;
                                    }
                                } else if (fieldType === "signature" && typeof val === "string" && val.startsWith("data:")) {
                                    // Signature stored as data URL in form state; upload once on submit
                                    try {
                                        const blob = base64ToBlob(val);
                                        const file = new File([blob], "signature.jpg", { type: "image/jpeg" });
                                        const response = await uploadFile(file);

                                        if (response?.data?.fileLink) {
                                            formDataToSend[key] = response.data.fileLink;
                                        } else {
                                            alert(`Signature upload failed for ${key}`);
                                            formDataToSend[key] = null;
                                        }
                                    } catch (err) {
                                        console.error("Error uploading signature on submit:", err);
                                        alert("Failed to upload signature. Please try again.");
                                        formDataToSend[key] = null;
                                    }
                                } else {
                                    // Keep normal values as-is (including existing signature URLs)
                                    formDataToSend[key] = val;
                                }
                            }

                            // Now the final payload
                            const data = {
                                formData: {
                                    ...formDataToSend,
                                    ...(hasSerialNoField ? { isManualVin } : {}),
                                },
                                doctype: decodedHeader,
                            };


                            // await updateProfile(data);
                            await updateDocument(data);

                            showModal({
                                title: "Success",
                                description: "Form submitted successfully!",
                            });
                            await refreshProfile();
                            await refreshDocuments();

                            // navigate('/forms',{replace: true});
                            navigate(-1);

                        } catch (error) {
                            console.error("Error while submitting form:", error);
                            alert("Something went wrong while uploading or submitting the form.");
                        } finally {
                            setSubmitting(false);
                        }
                    }}>
                    {({ setFieldValue, handleSubmit, isSubmitting }) => (
                        <Form className="space-y-5" onSubmit={handleSubmit}>
                            {formData?.fields?.length > 0 ? (
                                formData?.fields.map((field: any) => {
                                    const key = field.key;
                                    const type = field.type.toLowerCase();

                                    if (type === "file") {
                                        // Preview URL (string) and a flag indicating whether it is a PDF.
                                        // We keep a separate boolean because server URLs or blob/object URLs
                                        // may not reliably endWith(".pdf").
                                        const [preview, setPreview] = useState<string | null>(
                                            currentDoc?.data && currentDoc.data[key]
                                                ? currentDoc.data[key]
                                                : null
                                        );


                                        const [previewIsPdf, setPreviewIsPdf] = useState<boolean>(() => {
                                            const p = currentDoc?.data && currentDoc.data[key]
                                                ? String(currentDoc.data[key]).toLowerCase()
                                                : "";
                                            return p.includes(".pdf");
                                        });

                                        // Track total pages for inline PDF rendering
                                        const [numPages, setNumPages] = useState<number>(0);
                                        const viewerWidth = Math.min((typeof window !== 'undefined' ? window.innerWidth : 600) - 48, 800);

                                        useEffect(() => {
                                            // If the DB value changes (e.g., after update), update preview
                                            if (!preview && currentDoc?.data && currentDoc.data[key]) {
                                                const val = currentDoc.data[key];
                                                setPreview(val);
                                                const p = String(val).toLowerCase();
                                                setPreviewIsPdf(p.includes(".pdf"));
                                            }
                                            // eslint-disable-next-line
                                        }, [currentDoc]);

                                        // Identify placeholder type
                                        const showEligibilityPlaceholder = decodedHeader.toLowerCase() === "proof of work eligibility";
                                        const showLicencePlaceholder = decodedHeader.toLowerCase() === "driver licence";
                                        const showVehicleInsurancePlaceholder = decodedHeader.toLowerCase() === "vehicle insurance";
                                        const showVehicleInspectionPlaceholder = decodedHeader.toLowerCase() === "vehicle inspection";
                                        const showBackgroundCheckPlaceholder = decodedHeader.toLowerCase() === "background check";
                                        const showDriverAbstractPlaceholder = decodedHeader.toLowerCase() === "driver abstract";
                                        const showDriverVehicleRegistrationPlaceholder = decodedHeader.toLowerCase() === "vehicle registration";
                                        const showchildabuseregistrycheckPlaceholder = decodedHeader.toLowerCase() === "child abuse registry check";
                                        let placeholderSrc: string | null = null;
                                        let buttonLabel = "Select Document";

                                        if (showEligibilityPlaceholder) {
                                            // Use static asset from public so it works in both dev and production
                                            placeholderSrc = "/assets/proofOfWork.jpeg";
                                            buttonLabel = "Choose Pic";
                                        } else if (showLicencePlaceholder) {
                                            if (key.toLowerCase().includes("front")) {
                                                placeholderSrc = "/assets/dummydriverlicense.jpg"; // static asset for consistent placeholder in both dev and prod
                                                buttonLabel = "Choose Front Side Pic";
                                            } else if (key.toLowerCase().includes("back")) {
                                                placeholderSrc = "/assets/dummydriverlicense.jpg"; // static asset for consistent placeholder in both dev and prod
                                                buttonLabel = "Choose Back Side Pic";
                                            } else {
                                                buttonLabel = "Choose Pic";
                                            }
                                        }
                                        else if (showVehicleInspectionPlaceholder) {
                                            placeholderSrc = "/assets/vehicleInsepection.jpeg"; // static asset for consistent placeholder in both dev and prod
                                        }
                                        else if (showVehicleInsurancePlaceholder) {
                                            placeholderSrc = "/assets/vehicleinsurance.png"; // static asset for consistent placeholder in both dev and prod
                                        } else if (showDriverVehicleRegistrationPlaceholder) {
                                            placeholderSrc = "/assets/vehicleRegistration.png";
                                        }
                                        else if (showBackgroundCheckPlaceholder) {
                                            placeholderSrc = "/assets/backgroundCheck.svg";
                                            buttonLabel = "Choose Pic";
                                        }
                                        else if (showDriverAbstractPlaceholder) {
                                            placeholderSrc = "/assets/driverAbstract.svg";
                                            buttonLabel = "Choose Pic";
                                        } else if (showchildabuseregistrycheckPlaceholder) {
                                            placeholderSrc = "/assets/childabuse.png";
                                            buttonLabel = "Choose Pic";
                                        }

                                        const handleFileSelect = async (source: 'camera' | 'library' = 'library') => {
                                            const input = document?.createElement("input");
                                            input.type = "file";

                                            // Allow both images and PDFs for library, only images for camera
                                            input.accept = source === 'camera' ? "image/*" : "image/*,application/pdf";

                                            if (source === 'camera') {
                                                // Hint camera usage on mobile browsers
                                                input.setAttribute('capture', 'environment');
                                            }

                                            input.style.position = "fixed";
                                            input.style.top = "-10000px";
                                            input.style.left = "-10000px";
                                            input.value = "";
                                            document.body.appendChild(input);

                                            input.onchange = async (e: any) => {
                                                const file = e.target.files?.[0];
                                                if (!file) {
                                                    input.value = "";
                                                    document.body.removeChild(input);
                                                    return;
                                                }

                                                setUploadingFiles(prev => ({ ...prev, [key]: true }));
                                                setFileErrors(prev => ({ ...prev, [key]: "" }));

                                                try {
                                                    // Check if file is a PDF
                                                    const isPDF = file.type === "application/pdf" || file.name.toLowerCase().endsWith('.pdf');

                                                    if (isPDF) {
                                                        // PDF handling
                                                        if (source === 'camera') {
                                                            setFileErrors(prev => ({
                                                                ...prev,
                                                                [key]: "Camera capture only supports images. Please choose PDF from library."
                                                            }));
                                                            setUploadingFiles(prev => ({ ...prev, [key]: false }));
                                                            return;
                                                        }

                                                        // ✅ ACCEPT PDFs WITHOUT SIZE COMPRESSION
                                                        // Set the field value with the PDF file
                                                        setFieldValue(key, file);

                                                        // Create preview URL for PDF
                                                        const objectUrl = URL.createObjectURL(file);
                                                        setPreview(objectUrl);
                                                        setPreviewIsPdf(true);



                                                    } else if (file.type.startsWith("image/")) {
                                                        // Image handling (with compression)
                                                        let finalFile = file;

                                                        // Compress images if they're over 1MB
                                                        if (file.size > MAX_IMAGE_BYTES) {
                                                            try {
                                                                finalFile = await compressImage(file, MAX_IMAGE_BYTES);

                                                            } catch (err) {
                                                                console.error("Image compression failed:", err);
                                                                // Fall back to original file if compression fails
                                                            }
                                                        }

                                                        // Check if the (compressed) image is still too large
                                                        if (finalFile.size > MAX_IMAGE_BYTES) {
                                                            setFileErrors(prev => ({
                                                                ...prev,
                                                                [key]: `Image is too large (${(finalFile.size / 1024 / 1024).toFixed(2)}MB). Maximum size is 1MB.`
                                                            }));
                                                            setUploadingFiles(prev => ({ ...prev, [key]: false }));
                                                            return;
                                                        }

                                                        // Set the field value with the final (possibly compressed) image file
                                                        setFieldValue(key, finalFile);

                                                        // Create preview for image
                                                        const objectUrl = URL.createObjectURL(finalFile);
                                                        setPreview(objectUrl);
                                                        setPreviewIsPdf(false);

                                                    } else {
                                                        // Unsupported file type
                                                        setFileErrors(prev => ({
                                                            ...prev,
                                                            [key]: "Unsupported file type. Please select an image or PDF."
                                                        }));
                                                    }
                                                } catch (error) {
                                                    console.error("Error handling file:", error);
                                                    setFileErrors(prev => ({
                                                        ...prev,
                                                        [key]: "Failed to process file. Please try again."
                                                    }));
                                                } finally {
                                                    setUploadingFiles(prev => ({ ...prev, [key]: false }));
                                                    input.value = "";
                                                    setTimeout(() => {
                                                        if (input.parentNode) document.body.removeChild(input);
                                                    }, 0);
                                                }
                                            };

                                            input.click();
                                        };
                                        const isUploading = uploadingFiles[key] || false;
                                        return (
                                            <div key={key} className="space-y-3">
                                                {/* Preview or Placeholder */}
                                                <div
                                                    className="flex justify-center w-full cursor-pointer"
                                                    // onClick={() => handleFileSelect('library')}
                                                    style={{ minHeight: 220, maxHeight: 256 }} // 64 * 4 = 256px
                                                >
                                                    {preview ? (
                                                        previewIsPdf ? (
                                                            <div className="w-full overflow-auto rounded-md border border-gray-300 dark:border-gray-600" style={{ maxHeight: 256 }}>
                                                                <Document
                                                                    file={preview}
                                                                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                                                    onLoadError={(error) => {
                                                                        console.error("PDF loading error:", error);
                                                                        // Fallback to PDF icon
                                                                        setPreview("/assets/pdf_icon.png");
                                                                        setPreviewIsPdf(false);
                                                                    }}
                                                                    loading={<div className="p-4 text-center text-gray-500 dark:text-gray-400">Loading PDF…</div>}
                                                                    error={<div className="p-4 text-center text-red-500 dark:text-red-400">Failed to load PDF</div>}
                                                                >
                                                                    {Array.from({ length: numPages || 0 }, (_, i) => (
                                                                        <div key={`pdf_page_${i + 1}`} className="flex justify-center py-2">
                                                                            <Page
                                                                                pageNumber={i + 1}
                                                                                width={viewerWidth}
                                                                                renderTextLayer={false}
                                                                                onLoadError={(error) => {
                                                                                    console.error("Page loading error:", error);
                                                                                }}
                                                                                renderAnnotationLayer={false}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </Document>
                                                            </div>
                                                        ) : (
                                                            // ""
                                                            <ImageWithLoader
                                                                containerClassName="w-full h-full overflow-hidden"
                                                                src={preview}
                                                                alt="Selected document"
                                                                className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300 dark:border-gray-600"
                                                                onError={() => {
                                                                    // Handle image loading errors
                                                                    setPreview(null);
                                                                    setPreviewIsPdf(false);
                                                                    setFieldValue(key, null);
                                                                }}
                                                            />
                                                        )
                                                    ) : placeholderSrc ? (
                                                        <ImageWithLoader
                                                            containerClassName="w-full h-full overflow-hidden"
                                                            onError={() => {
                                                                // Handle image loading errors
                                                                setPreview(null);
                                                                setPreviewIsPdf(false);
                                                                setFieldValue(key, null);
                                                            }}
                                                            src={placeholderSrc ? placeholderSrc : "/assets/car.png"}
                                                            alt="Select image"
                                                            className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300 dark:border-gray-600 "
                                                        />
                                                    ) : (
                                                        <div className="w-full h-48 flex items-center justify-center border-2 border-dashed border-gray-400 dark:border-gray-600 rounded-md hover:border-gray-600 dark:hover:border-gray-500">
                                                            <Plus className="w-12 h-12 text-gray-400 dark:text-gray-500" />
                                                        </div>
                                                    )}
                                                </div>
                                                {/* <div
                                                className="flex justify-center w-full cursor-pointer"
                                                onClick={handleFileSelect}
                                            >
                                                {preview ? (
                                                    // Show image or PDF icon based on file type
                                                    preview.endsWith(".pdf") ? (
                                                        <img
                                                            src="/assets/pdf_icon.png"
                                                            alt="PDF document"
                                                            className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300"
                                                        />
                                                    ) : (
                                                        <img
                                                            src={preview}
                                                            alt="Selected document"
                                                            className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300"
                                                        />
                                                    )
                                                ) : placeholderSrc ? (
                                                    <img
                                                        src={placeholderSrc}
                                                        alt="Placeholder"
                                                        className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300"
                                                    />
                                                ) : (
                                                    <div className="w-full h-48 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md hover:border-gray-600">
                                                        <Plus className="w-12 h-12 text-gray-400" />
                                                    </div>
                                                )}
                                            </div> */}
                                                {/* <div
                                                className="flex justify-center w-full cursor-pointer"
                                                onClick={handleFileSelect}
                                            >
                                                {preview ? (
                                                    <img
                                                        src={preview}
                                                        alt="Selected document"
                                                        className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300"
                                                    />
                                                ) : placeholderSrc ? (
                                                    <img
                                                        src={placeholderSrc}
                                                        alt="Placeholder"
                                                        className="w-full h-64 object-contain opacity-80 rounded-md border border-gray-300"
                                                    />
                                                ) : (
                                                    <div className="w-full h-48 flex items-center justify-center border-2 border-dashed border-gray-400 rounded-md hover:border-gray-600">
                                                        <Plus className="w-12 h-12 text-gray-400" />
                                                    </div>
                                                )}
                                            </div> */}

                                                {/* Direct Button */}
                                                {showBackgroundCheckPlaceholder || showDriverAbstractPlaceholder ? (
                                                    <div className="flex flex-col gap-3 mt-6">
                                                        {/* <Button
                                                            className="w-full"
                                                            type="button"
                                                            onClick={() => handleFileSelect('camera')}
                                                        >
                                                            Take a photo
                                                        </Button> */}
                                                        <Button
                                                            className="w-full"
                                                            type="button"

                                                            onClick={() => handleFileSelect('library')}
                                                        >
                                                            Choose from library
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        className="w-full mt-0"
                                                        type="button"
                                                        onClick={() => handleFileSelect('library')}
                                                    >
                                                        {buttonLabel}
                                                    </Button>
                                                )}

                                                {fileErrors[key] ? (
                                                    <div className="text-red-600 dark:text-red-400 text-sm mt-2 text-center">{fileErrors[key]}</div>
                                                ) : null}

                                                <ErrorMessage
                                                    name={key}
                                                    component="div"
                                                    className="text-red-600 dark:text-red-400 text-sm mt-1 text-center"
                                                />
                                            </div>
                                        );
                                    }
                                    if (type === "url") {


                                        const url = field.iframeUrl || field.url || field.value;
                                        if (!url) return null;
                                        return (
                                            <div
                                                key={key}
                                                className="mb-4 w-full flex justify-center"
                                            >
                                                <div
                                                    className="w-full max-w-3xl mx-auto my-4 rounded-lg shadow-md bg-white dark:bg-gray-800 overflow-hidden"
                                                    style={{ maxHeight: '70vh' }}
                                                >
                                                    <iframe
                                                        src={url}
                                                        title={field.label || key || "iframe"}
                                                        width="100%"
                                                        style={{
                                                            border: 'none',
                                                            display: 'block',
                                                            background: 'white',
                                                            height: '70vh',
                                                            width: '100%',
                                                        }}
                                                        allowFullScreen
                                                    />
                                                </div>
                                            </div>
                                            // <div key={key} className="w-full ">
                                            //     <iframe
                                            //         src={url}
                                            //         title={field.label || key || "iframe"}
                                            //         width="100%"
                                            //         height="100vh"
                                            //         // height="600px"

                                            //         allowFullScreen
                                            //     />
                                            // </div>
                                        );
                                    }
                                    if (type === "signature") {
                                        const sigCanvas = useRef<ReactSignatureCanvas | null>(null);

                                        const existingSignatureUrl: string | null =
                                            currentDoc?.data && currentDoc.data[key]
                                                ? String(currentDoc.data[key])
                                                : null;

                                        useEffect(() => {
                                            const canvas = sigCanvas.current;
                                            if (!canvas || !existingSignatureUrl) return;

                                            let cancelled = false;

                                            const drawFromDataUrl = (dataUrl: string) => {
                                                try {
                                                    if (!cancelled) {

                                                        canvas.clear();
                                                        canvas.fromDataURL(dataUrl);
                                                    }
                                                } catch (e) {
                                                    console.error("Failed to render saved signature on canvas:", e);
                                                }
                                            };
                                            if (existingSignatureUrl.startsWith("data:")) {
                                                drawFromDataUrl(existingSignatureUrl);
                                            } else {
                                                (async () => {
                                                    try {
                                                        const resp = await fetch(existingSignatureUrl, { credentials: 'omit' });
                                                        if (!resp.ok) return;
                                                        const blob = await resp.blob();
                                                        const reader = new FileReader();
                                                        reader.onloadend = () => {
                                                            if (typeof reader.result === 'string') {
                                                                drawFromDataUrl(reader.result);
                                                            }
                                                        };
                                                        reader.readAsDataURL(blob);
                                                    } catch (err) {
                                                        console.error("Error preloading signature image:", err);
                                                    }
                                                })();
                                            }

                                            return () => {
                                                cancelled = true;
                                            };
                                        }, [existingSignatureUrl]);


                                        const handleClearSignature = () => {
                                            sigCanvas.current?.clear();
                                            // Clear signature value in form state; no API call here
                                            setFieldValue(key, null);
                                        };

                                        const handleSignatureEnd = () => {
                                            try {
                                                const canvas = sigCanvas.current;

                                                if (canvas && !canvas.isEmpty()) {
                                                    // Store signature as data URL in form state; upload happens on submit
                                                    const sig = canvas.toDataURL("image/png");
                                                    setFieldValue(key, sig);
                                                } else {
                                                    setFieldValue(key, null);
                                                }
                                            } catch (error) {
                                                console.error("🖊️ Error capturing signature:", error);
                                            }
                                        };

                                        return (
                                            <div key={key} className="space-y-3">
                                                <label className="block font-medium mb-1">
                                                    {field.label}{" "}
                                                    {field.required && <span className="text-red-500">*</span>}
                                                </label>


                                                <div className="border border-gray-400 dark:border-gray-600 rounded-md overflow-hidden">
                                                    <SignatureCanvas
                                                        confirmText={true ? "Processing..." : "Save"}
                                                        ref={sigCanvas}
                                                        loading={true}
                                                        {...({
                                                            onEnd: handleSignatureEnd
                                                        } as any)}
                                                        canvasProps={{
                                                            width: 790,
                                                            height: 200,
                                                            className: "bg-white rounded-md",
                                                        }}
                                                    />
                                                </div>

                                                {/* Signature Controls */}
                                                <div className="flex justify-between mt-3">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        onClick={handleClearSignature}
                                                        className="w-full"
                                                    >
                                                        Clear
                                                    </Button>
                                                </div>

                                                <ErrorMessage
                                                    name={key}
                                                    component="div"
                                                    className="text-red-600 text-sm mt-1"
                                                />
                                            </div>
                                        );
                                    }

                                    if (type === "checkbox") {

                                        const handleCheckboxChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const isChecked = e.target.checked;
                                            const previousValue = !!currentDoc?.data?.[key];

                                            setFieldValue(key, isChecked);
                                            try {
                                                const data = {
                                                    formData: { [key]: isChecked },
                                                    doctype: decodedHeader,
                                                };


                                                // await updateProfile(data);
                                                // await updateDocument(data);
                                                // await refreshDocuments();

                                            } catch (error) {
                                                console.error(`Error saving checkbox ${key}:`, error);
                                                setFieldValue(key, previousValue);
                                            }
                                        };

                                        return (
                                            <div key={key} className="space-y-3">
                                                <div className="flex items-start gap-3 align-center">
                                                    <Field name={key}>
                                                        {({ field }: any) => (
                                                            <input
                                                                type="checkbox"
                                                                id={key}
                                                                checked={field.value || false}
                                                                onChange={handleCheckboxChange}
                                                                className="mt-1 h-5 w-5 accent-blue-600 cursor-pointer"
                                                            />
                                                        )}
                                                    </Field>
                                                    <label htmlFor={key} className="text-base font-medium leading-snug cursor-pointer">
                                                        {field.label}{" "}
                                                        {field.required && <span className="text-red-500">*</span>}
                                                    </label>
                                                </div>

                                                <ErrorMessage
                                                    name={key}
                                                    component="div"
                                                    className="text-red-600 text-sm mt-1"
                                                />
                                            </div>
                                        );
                                    }

                                    if (type === "radio") {
                                        return (
                                            <div key={key} className="space-y-3">
                                                <label className="block font-medium mb-2">
                                                    {field.label}{" "}
                                                    {field.required && <span className="text-red-500">*</span>}
                                                </label>

                                                <Field name={key}>
                                                    {({ field: formikField }: any) => (
                                                        <div className="gap-x-4 flex flex-row">
                                                            {field.options?.map((option: any, idx: number) => (
                                                                <div key={option._id || idx} className="flex flex-row items-center gap-3">
                                                                    <input
                                                                        type="radio"
                                                                        id={`${key}-${option._id || idx}`}
                                                                        name={key}
                                                                        value={option.value}
                                                                        checked={formikField.value === option.value}
                                                                        onChange={() => setFieldValue(key, option.value)}
                                                                        className="h-4 w-4 accent-blue-600 cursor-pointer"
                                                                    />
                                                                    <label
                                                                        htmlFor={`${key}-${option._id || idx}`}
                                                                        className="text-sm font-medium cursor-pointer"
                                                                    >
                                                                        {option.label}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </Field>
                                                <ErrorMessage
                                                    name={key}
                                                    component="div"
                                                    className="text-red-600 text-sm mt-1"
                                                />
                                            </div>
                                        );
                                    }

                                    if (type === "date") {

                                        return (
                                            <div key={key} className="space-y-2 w-full">
                                                <label className="block font-medium mb-1">
                                                    {field.label || key}
                                                    {field.required && <span className="text-red-500">*</span>}
                                                </label>

                                                <Field name={key}>
                                                    {({ field, form }: any) => {
                                                        const valueDate = field.value ? new Date(field.value) : undefined;
                                                        const valueForInput = valueDate
                                                            ? valueDate.toISOString().slice(0, 10)
                                                            : "";

                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        const todayStr = new Date(
                                                            today.getTime() - today.getTimezoneOffset() * 60000
                                                        )
                                                            .toISOString()
                                                            .slice(0, 10);
                                                        const isVehicleInspection =
                                                            decodedHeader.toLowerCase() === "vehicle inspection";
                                                        const isProofOfWorkEligibility =
                                                            decodedHeader.toLowerCase() === "proof of work eligibility";

                                                        const minAttr = isProofOfWorkEligibility
                                                            ? todayStr
                                                            : isVehicleInspection
                                                                ? undefined
                                                                : todayStr;

                                                        const maxAttr = isVehicleInspection ? todayStr : undefined;

                                                        return (
                                                            <input
                                                                type="date"
                                                                {...field}
                                                                value={valueForInput}
                                                                min={minAttr}
                                                                max={maxAttr}
                                                                onChange={(e) => {
                                                                    const selectedDate = e.target.value;

                                                                    // Enforce min/max manually so it also works on iOS
                                                                    if (!selectedDate) {
                                                                        form.setFieldValue(key, "");
                                                                        return;
                                                                    }

                                                                    let finalDateStr = selectedDate; // YYYY-MM-DD

                                                                    if (minAttr && finalDateStr < minAttr) {
                                                                        finalDateStr = minAttr;
                                                                    }

                                                                    if (maxAttr && finalDateStr > maxAttr) {
                                                                        finalDateStr = maxAttr;
                                                                    }

                                                                    form.setFieldValue(
                                                                        key,
                                                                        new Date(finalDateStr).toISOString()
                                                                    );
                                                                }}
                                                                // className="block w-full max-w-full border rounded-md px-3 py-2 box-border"
                                                                className="block w-full min-w-0 border dark:border-gray-600 rounded-md px-3 py-2 box-border appearance-none dark:bg-gray-700 dark:text-white"
                                                            />
                                                        );
                                                    }}
                                                </Field>

                                                <ErrorMessage
                                                    name={key}
                                                    component="div"
                                                    className="text-red-600 text-sm mt-1"
                                                />
                                            </div>
                                        );
                                    }

                                    if (type?.toLowerCase() === "accordion") {
                                        const items = Array.isArray(field?.accordionItems)
                                            ? field?.accordionItems
                                            : [field?.accordionItems];

                                        return (
                                            <Accordion type="single" collapsible className="mb-4 w-full" key={key}>
                                                {items.map((item: any, idx: number) => (
                                                    <AccordionItem value={`item-${idx}`} key={idx}>
                                                        <AccordionTrigger>{item?.title}</AccordionTrigger>
                                                        <AccordionContent className="flex flex-col gap-4 text-balance">{item?.content}</AccordionContent>
                                                    </AccordionItem>
                                                ))}
                                            </Accordion>
                                        );
                                    }
                                    if (type === "selectandinput") {
                                        if (type === "selectandinput") {
                                            if (key === "vehicleMake") {
                                                return (
                                                    <div key={key} className="space-y-2">
                                                        <label className="block font-medium mb-1">
                                                            {field.label}{" "}
                                                            {field.required && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <Field name={key}>
                                                            {({ field: fField, form }: any) => {
                                                                const hasPlateAndSerial =
                                                                    !!String(form.values?.plateNo ?? "").trim() &&
                                                                    !!String(form.values?.serialNo ?? "").trim();
                                                                const currentValue = form.values[key];
                                                                const displayLabel = useCustomVehicleMake
                                                                    ? (currentValue || "Other")
                                                                    : (currentValue || field.placeholder || "Select make");
                                                                const options = vehicleBrands || [];
                                                                return (
                                                                    <>
                                                                        <Popover
                                                                            open={!!openDropdowns[key]}
                                                                            onOpenChange={(open) =>
                                                                                setOpenDropdowns((prev) => ({ ...prev, [key]: open }))
                                                                            }
                                                                        >
                                                                            <PopoverTrigger asChild>
                                                                                <Button
                                                                                    variant="outline"
                                                                                    className="w-full justify-between"
                                                                                    type="button"
                                                                                    disabled={!hasPlateAndSerial || loadingBrands}
                                                                                >
                                                                                    <span className="truncate">{displayLabel}</span>
                                                                                </Button>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent className="w-full max-h-60 overflow-y-auto p-0">
                                                                                {options.map((opt: any, idx: number) => {
                                                                                    const label = getBrandLabel(opt);
                                                                                    return (
                                                                                        <button
                                                                                            key={label + idx}
                                                                                            className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700"
                                                                                            type="button"
                                                                                            onClick={async () => {
                                                                                                setUseCustomVehicleMake(false);

                                                                                                // Update make and clear dependent selections
                                                                                                const brandLabel = label;
                                                                                                form.setFieldValue(key, brandLabel);
                                                                                                form.setFieldValue("vehicleModel", "");
                                                                                                form.setFieldValue("vehicleYear", "");
                                                                                                form.setFieldValue("vehicleColor", "");
                                                                                                form.setFieldValue("vehicleColorCode", "");
                                                                                                setUseCustomVehicleYear(false);
                                                                                                setUseCustomVehicleColor(false);

                                                                                                setVehicleModels([]);
                                                                                                setModelYears([]);
                                                                                                setModelColors([]);

                                                                                                setOpenDropdowns((prev) => ({ ...prev, [key]: false }));

                                                                                                if (opt?._id || opt?.id || opt?.value) {
                                                                                                    const brandId = opt?._id ?? opt?.id ?? opt?.value;
                                                                                                    await fetchModelsForBrand(String(brandId));
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {label}
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                                <button
                                                                                    type="button"
                                                                                    className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700 border-t dark:border-gray-700"
                                                                                    onClick={() => {
                                                                                        setUseCustomVehicleMake(true);
                                                                                        setVehicleModels([]);
                                                                                        setModelYears([]);
                                                                                        setModelColors([]);

                                                                                        // Clear make and all dependents when switching to Other
                                                                                        form.setFieldValue(key, "");
                                                                                        form.setFieldValue("vehicleModel", "");
                                                                                        form.setFieldValue("vehicleYear", "");
                                                                                        form.setFieldValue("vehicleColor", "");
                                                                                        form.setFieldValue("vehicleColorCode", "");
                                                                                        setUseCustomVehicleYear(false);
                                                                                        setUseCustomVehicleColor(false);

                                                                                        setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                    }}
                                                                                >
                                                                                    Other
                                                                                </button>
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                        {useCustomVehicleMake && (
                                                                            <Input
                                                                                disabled={loadingBrands || !hasPlateAndSerial}
                                                                                type="text"
                                                                                placeholder={field.placeholder || "Enter vehicle make"}
                                                                                {...fField}
                                                                                value={currentValue || ""}
                                                                                onChange={(e) => {
                                                                                    // Changing make text should also clear dependent fields
                                                                                    form.setFieldValue(key, e.target.value);
                                                                                    form.setFieldValue("vehicleModel", "");
                                                                                    form.setFieldValue("vehicleYear", "");
                                                                                    form.setFieldValue("vehicleColor", "");
                                                                                    form.setFieldValue("vehicleColorCode", "");
                                                                                    setUseCustomVehicleYear(false);
                                                                                    setUseCustomVehicleColor(false);

                                                                                    setVehicleModels([]);
                                                                                    setModelYears([]);
                                                                                    setModelColors([]);
                                                                                }}
                                                                                className="mt-2"
                                                                            />
                                                                        )}
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>

                                                        <ErrorMessage
                                                            name={key}
                                                            component="div"
                                                            className="text-red-600 text-sm mt-1"
                                                        />
                                                    </div>
                                                );
                                            }

                                            if (key === "vehicleModel") {
                                                const shouldUseInput = useCustomVehicleMake || useCustomVehicleModel || vehicleModels.length === 0;
                                                return (
                                                    <div key={key} className="space-y-2">
                                                        <label className="block font-medium mb-1">
                                                            {field.label}{" "}
                                                            {field.required && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <Field name={key}>
                                                            {({ field: fField, form }: any) => {
                                                                const hasPlateAndSerial =
                                                                    !!String(form.values?.plateNo ?? "").trim() &&
                                                                    !!String(form.values?.serialNo ?? "").trim();
                                                                const currentValue = form.values[key];
                                                                if (shouldUseInput) {
                                                                    return (
                                                                        <>
                                                                            <Input
                                                                                type="text"
                                                                                placeholder={field.placeholder || "Enter vehicle model"}
                                                                                {...fField}
                                                                                disabled={loadingModels || loadingBrands || !form.values.vehicleMake || !hasPlateAndSerial}
                                                                                value={currentValue || ""}
                                                                                onChange={(e) => {
                                                                                    // Free-typed model: clear year/color since model changed
                                                                                    form.setFieldValue(key, e.target.value);
                                                                                    form.setFieldValue("vehicleYear", "");
                                                                                    form.setFieldValue("vehicleColor", "");
                                                                                    form.setFieldValue("vehicleColorCode", "");
                                                                                    setUseCustomVehicleColor(false);

                                                                                    setModelYears([]);
                                                                                    setModelColors([]);
                                                                                }}
                                                                            />
                                                                        </>
                                                                    );
                                                                }

                                                                const currentLabel = currentValue || field.placeholder || "Select model";
                                                                return (
                                                                    <>
                                                                        <Popover
                                                                            open={!!openDropdowns[key]}
                                                                            onOpenChange={(open) =>
                                                                                setOpenDropdowns((prev) => ({ ...prev, [key]: open }))
                                                                            }
                                                                        >
                                                                            <PopoverTrigger asChild>
                                                                                <Button
                                                                                    variant="outline"
                                                                                    className="w-full justify-between"
                                                                                    type="button"
                                                                                    disabled={loadingModels || loadingBrands || !form.values.vehicleMake || !hasPlateAndSerial}
                                                                                >
                                                                                    <span className="truncate">{currentLabel}</span>
                                                                                </Button>
                                                                            </PopoverTrigger>
                                                                            <PopoverContent className="w-full max-h-60 overflow-y-auto p-0">
                                                                                {vehicleModels.map((opt: any, idx: number) => {
                                                                                    const label = getModelLabel(opt);
                                                                                    return (
                                                                                        <button
                                                                                            key={label + idx}
                                                                                            type="button"
                                                                                            className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700"
                                                                                            onClick={() => {
                                                                                                setUseCustomVehicleModel(false);

                                                                                                // Update model and clear year/color
                                                                                                form.setFieldValue(key, label);
                                                                                                form.setFieldValue("vehicleYear", "");
                                                                                                form.setFieldValue("vehicleColor", "");
                                                                                                form.setFieldValue("vehicleColorCode", "");
                                                                                                setUseCustomVehicleYear(false);
                                                                                                setUseCustomVehicleColor(false);

                                                                                                setModelYears([]);
                                                                                                setModelColors([]);

                                                                                                setOpenDropdowns((prev) => ({ ...prev, [key]: false }));

                                                                                                if (selectedBrandId) {

                                                                                                    const modelKey =
                                                                                                        opt?.modelName;
                                                                                                    fetchYearsForModel(selectedBrandId, String(modelKey));
                                                                                                }
                                                                                            }}
                                                                                        >
                                                                                            {label}
                                                                                        </button>
                                                                                    );
                                                                                })}
                                                                                <button
                                                                                    type="button"
                                                                                    className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700 border-t dark:border-gray-700"
                                                                                    onClick={() => {
                                                                                        setUseCustomVehicleModel(true);
                                                                                        setModelYears([]);
                                                                                        setModelColors([]);

                                                                                        // Switching model to Other: clear model/year/color
                                                                                        form.setFieldValue(key, "");
                                                                                        form.setFieldValue("vehicleYear", "");
                                                                                        form.setFieldValue("vehicleColor", "");
                                                                                        form.setFieldValue("vehicleColorCode", "");
                                                                                        setUseCustomVehicleYear(false);
                                                                                        setUseCustomVehicleColor(false);

                                                                                        setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                    }}
                                                                                >
                                                                                    Other
                                                                                </button>
                                                                            </PopoverContent>
                                                                        </Popover>
                                                                    </>
                                                                );
                                                            }}
                                                        </Field>

                                                        <ErrorMessage
                                                            name={key}
                                                            component="div"
                                                            className="text-red-600 text-sm mt-1"
                                                        />
                                                    </div>
                                                );
                                            }

                                            if (key === "vehicleYear") {
                                                const shouldUseInput = useCustomVehicleMake || useCustomVehicleModel || useCustomVehicleYear || modelYears.length === 0;

                                                return (
                                                    <div key={key} className="space-y-2">
                                                        <label className="block font-medium mb-1">
                                                            {field.label}{" "}
                                                            {field.required && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <Field name={key}>
                                                            {({ field: fField, form }: any) => {
                                                                const hasPlateAndSerial =
                                                                    !!String(form.values?.plateNo ?? "").trim() &&
                                                                    !!String(form.values?.serialNo ?? "").trim();
                                                                const currentValue = form.values[key];
                                                                if (shouldUseInput) {
                                                                    return (
                                                                        <Input
                                                                            type="text"
                                                                            inputMode="numeric"
                                                                            pattern="[0-9]*"
                                                                            placeholder={field.placeholder || "Enter vehicle year"}
                                                                            {...fField}
                                                                            disabled={loadingYears || loadingModels || !form.values.vehicleMake || !form.values.vehicleModel || !hasPlateAndSerial}
                                                                            value={currentValue || ""}
                                                                            onChange={(e) => {
                                                                                const digitsOnly = e.target.value.replace(/\D/g, "");
                                                                                form.setFieldValue(key, digitsOnly);
                                                                                // Free-typed year: clear color and any year-specific colors
                                                                                form.setFieldValue("vehicleColor", "");
                                                                                form.setFieldValue("vehicleColorCode", "");
                                                                                setUseCustomVehicleColor(false);
                                                                                setModelColors([]);

                                                                                if (
                                                                                    digitsOnly.length === 4 &&
                                                                                    selectedBrandId &&
                                                                                    form.values.vehicleModel
                                                                                ) {
                                                                                    fetchYearsForModel(
                                                                                        selectedBrandId,
                                                                                        String(form.values.vehicleModel),
                                                                                        digitsOnly
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    );
                                                                }

                                                                const currentLabel = currentValue || field.placeholder || "Select year";
                                                                return (
                                                                    <Popover
                                                                        open={!!openDropdowns[key]}
                                                                        onOpenChange={(open) =>
                                                                            setOpenDropdowns((prev) => ({ ...prev, [key]: open }))
                                                                        }
                                                                    >
                                                                        <PopoverTrigger asChild>
                                                                            <Button
                                                                                variant="outline"
                                                                                className="w-full justify-between"
                                                                                type="button"
                                                                                disabled={loadingYears || loadingModels || !form.values.vehicleMake || !form.values.vehicleModel || !hasPlateAndSerial}
                                                                            >
                                                                                <span className="truncate">{currentLabel}</span>
                                                                            </Button>
                                                                        </PopoverTrigger>
                                                                        <PopoverContent className="w-full max-h-60 overflow-y-auto p-0">
                                                                            {modelYears.map((yearObj: any, idx: number) => {
                                                                                const label = getYearLabel(yearObj);
                                                                                const value = yearObj?.year ?? yearObj?.value ?? label;
                                                                                return (
                                                                                    <button
                                                                                        key={label + idx}
                                                                                        type="button"
                                                                                        className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700"
                                                                                        onClick={() => {
                                                                                            // Set selected year and reset color, then load colors for that year
                                                                                            setUseCustomVehicleYear(false);
                                                                                            form.setFieldValue(key, value);
                                                                                            form.setFieldValue("vehicleColor", "");
                                                                                            form.setFieldValue("vehicleColorCode", "");
                                                                                            setUseCustomVehicleColor(false);
                                                                                            if (selectedBrandId && form.values.vehicleModel) {
                                                                                                fetchYearsForModel(
                                                                                                    selectedBrandId,
                                                                                                    String(form.values.vehicleModel),
                                                                                                    String(value)
                                                                                                );
                                                                                            }
                                                                                            if (Array.isArray(yearObj?.colors)) {
                                                                                                setModelColors(yearObj.colors);
                                                                                            } else {
                                                                                                setModelColors([]);
                                                                                            }
                                                                                            setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                        }}
                                                                                    >
                                                                                        {label}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                            <button
                                                                                type="button"
                                                                                className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700 border-t dark:border-gray-700"
                                                                                onClick={() => {
                                                                                    setUseCustomVehicleYear(true);
                                                                                    form.setFieldValue(key, "");
                                                                                    form.setFieldValue("vehicleColor", "");
                                                                                    form.setFieldValue("vehicleColorCode", "");
                                                                                    setUseCustomVehicleColor(false);
                                                                                    setModelColors([]);
                                                                                    setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                }}
                                                                            >
                                                                                Other
                                                                            </button>
                                                                        </PopoverContent>
                                                                    </Popover>
                                                                );
                                                            }}
                                                        </Field>

                                                        <ErrorMessage
                                                            name={key}
                                                            component="div"
                                                            className="text-red-600 text-sm mt-1"
                                                        />
                                                    </div>
                                                );
                                            }
                                            if (key === "vehicleColor") {
                                                // If backend provides colors (including default/null-year colors),
                                                // always show them in dropdown even for custom make/model paths.
                                                const shouldUseInput = modelColors.length === 0;
                                                const normalizeHexInput = (raw: string) => {
                                                    const stripped = raw.replace(/^#+/, "").replace(/[^A-Fa-f0-9]/g, "").slice(0, 6);
                                                    return stripped ? `#${stripped.toUpperCase()}` : "";
                                                };
                                                const toSixDigitHex = (hex: string) => {
                                                    if (/^#[A-Fa-f0-9]{6}$/.test(hex)) return hex.toUpperCase();
                                                    if (/^#[A-Fa-f0-9]{3}$/.test(hex)) {
                                                        const h = hex.slice(1).toUpperCase();
                                                        return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
                                                    }
                                                    return "#000000";
                                                };

                                                return (
                                                    <div key={key} className="space-y-2">
                                                        <label className="block font-medium mb-1">
                                                            {field.label}{" "}
                                                            {field.required && <span className="text-red-500">*</span>}
                                                        </label>
                                                        <Field name={key}>
                                                            {({ field: fField, form }: any) => {
                                                                const currentValue = form.values[key];
                                                                const isManualColorEntry = shouldUseInput || useCustomVehicleColor;
                                                                if (isManualColorEntry) {
                                                                    const colorCodeValue = String(form.values.vehicleColorCode || "");
                                                                    const hasHexError =
                                                                        colorCodeValue.length > 0 &&
                                                                        !/^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(colorCodeValue);
                                                                    const colorPickerValue = toSixDigitHex(colorCodeValue);
                                                                    return (
                                                                        <div className="space-y-2">
                                                                            <Input
                                                                                disabled={loadingYears || loadingModels || !form.values.vehicleMake || !form.values.vehicleModel || !form.values.vehicleYear}
                                                                                type="text"
                                                                                placeholder={field.placeholder || "Enter vehicle color name"}
                                                                                {...fField}
                                                                                value={currentValue || ""}
                                                                                onChange={(e) => {
                                                                                    form.setFieldValue(key, e.target.value);
                                                                                }}
                                                                            />
                                                                            <div className="flex items-center gap-2">
                                                                                <Input
                                                                                    disabled={loadingYears || loadingModels || !form.values.vehicleMake || !form.values.vehicleModel || !form.values.vehicleYear}
                                                                                    type="text"
                                                                                    placeholder="Enter hex color (e.g. #FFFFFF)"
                                                                                    value={colorCodeValue}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue("vehicleColorCode", normalizeHexInput(e.target.value));
                                                                                    }}
                                                                                />
                                                                                <input
                                                                                    disabled={loadingYears || loadingModels || !form.values.vehicleMake || !form.values.vehicleModel || !form.values.vehicleYear}
                                                                                    type="color"
                                                                                    aria-label="Pick vehicle color"
                                                                                    value={colorPickerValue}
                                                                                    onChange={(e) => {
                                                                                        form.setFieldValue("vehicleColorCode", e.target.value.toUpperCase());
                                                                                    }}
                                                                                    className="h-10 w-14 cursor-pointer rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-1"
                                                                                />
                                                                            </div>
                                                                            {hasHexError && (
                                                                                <div className="text-red-600 text-sm mt-1">Enter a valid hex color like #FFFFFF.</div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                }

                                                                const currentLabel = currentValue || field.placeholder || "Select color";
                                                                const selectedColor = modelColors.find((color: any) => {
                                                                    const value = color?.name ?? getColorLabel(color);
                                                                    return String(value) === String(currentValue);
                                                                });
                                                                const selectedColorHex = getDisplayHexColor(selectedColor);
                                                                return (
                                                                    <Popover
                                                                        open={!!openDropdowns[key]}
                                                                        onOpenChange={(open) => setOpenDropdowns((prev) => ({ ...prev, [key]: open }))}>
                                                                        <PopoverTrigger asChild>
                                                                            <Button variant="outline" className="w-full justify-between" type="button">
                                                                                <span className="truncate flex items-center gap-2">
                                                                                    {selectedColorHex && (
                                                                                        <span
                                                                                            className="inline-block w-3 h-3 rounded-full border border-gray-300"
                                                                                            style={{ backgroundColor: selectedColorHex }}
                                                                                        />
                                                                                    )}
                                                                                    {currentLabel}
                                                                                </span>
                                                                            </Button>
                                                                        </PopoverTrigger>
                                                                        <PopoverContent className="w-full max-h-60 overflow-y-auto p-0">
                                                                            {modelColors.map((color: any, idx: number) => {
                                                                                const label = getColorLabel(color);
                                                                                const hex = getDisplayHexColor(color);
                                                                                const value = color?.name ?? label;
                                                                                const image = color?.image ?? null;
                                                                                return (
                                                                                    <button
                                                                                        key={label + idx}
                                                                                        type="button"
                                                                                        className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700 flex items-center gap-2"
                                                                                        onClick={() => {
                                                                                            // Store both color name and hex code (if available)
                                                                                            setUseCustomVehicleColor(false);
                                                                                            form.setFieldValue(key, value);
                                                                                            form.setFieldValue("vehicleColorCode", hex || "");
                                                                                            form.setFieldValue("vehicleColorImage", image || "");
                                                                                            setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                        }}
                                                                                    >
                                                                                        {hex && (
                                                                                            <span
                                                                                                className="inline-block w-3 h-3 rounded-full border border-gray-300"
                                                                                                style={{ backgroundColor: hex }}
                                                                                            />
                                                                                        )}
                                                                                        {label}
                                                                                    </button>
                                                                                );
                                                                            })}
                                                                            <button
                                                                                type="button"
                                                                                className="w-full text-left px-4 py-2 hover:bg-muted dark:hover:bg-gray-700 border-t dark:border-gray-700"
                                                                                onClick={() => {
                                                                                    setUseCustomVehicleColor(true);
                                                                                    form.setFieldValue(key, "");
                                                                                    form.setFieldValue("vehicleColorCode", "");
                                                                                    form.setFieldValue("vehicleColorImage", "");
                                                                                    setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                }}
                                                                            >
                                                                                Other
                                                                            </button>
                                                                        </PopoverContent>
                                                                    </Popover>
                                                                );
                                                            }}
                                                        </Field>

                                                        <ErrorMessage
                                                            name={key}
                                                            component="div"
                                                            className="text-red-600 text-sm mt-1"
                                                        />
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={key} className="space-y-2">
                                                    <label className="block font-medium mb-1">
                                                        {field.label}{" "}
                                                        {field.required && <span className="text-red-500">*</span>}
                                                    </label>
                                                    <Field name={key}>
                                                        {({ field: fField, form }: any) => {
                                                            const isSerialNoDisabled =
                                                                key === "serialNo" &&
                                                                (!String(form.values?.plateNo ?? "").trim() || isFetchingVin || isDecodingVin);
                                                            if (key === "plateNo") {
                                                                return (
                                                                    <Input
                                                                        type="text"
                                                                        placeholder={field.placeholder || ""}
                                                                        {...fField}
                                                                        onBlur={async (e) => {
                                                                            fField.onBlur(e);
                                                                            const plateValue = e.target.value;
                                                                            if (!plateValue?.trim()) return;

                                                                            const vinNo = await fetchVinByPlateNo(plateValue);
                                                                            if (!vinNo) {
                                                                                setAutoFilledVin("");
                                                                                return;
                                                                            }

                                                                            form.setFieldValue("serialNo", vinNo);
                                                                            setAutoFilledVin(vinNo);
                                                                            setIsManualVin(false);

                                                                            await populateVehicleFieldsFromVin(vinNo, form);
                                                                        }}
                                                                    />
                                                                );
                                                            }

                                                            return (
                                                                <Input
                                                                    type="text"
                                                                    placeholder={field.placeholder || ""}
                                                                    {...fField}
                                                                    disabled={isSerialNoDisabled}
                                                                    onChange={(e) => {
                                                                        fField.onChange(e);
                                                                        if (key === "serialNo") {
                                                                            const currentValue = (e.target.value?.trim?.() || "").toUpperCase();
                                                                            const autoValue = autoFilledVin?.trim?.() || "";
                                                                            setIsManualVin(currentValue !== autoValue);
                                                                            if (currentValue.length === 17) {
                                                                                void populateVehicleFieldsFromVin(currentValue, form);
                                                                            }
                                                                        }
                                                                    }}
                                                                />
                                                            );
                                                        }}
                                                    </Field>
                                                    <ErrorMessage
                                                        name={key}
                                                        component="div"
                                                        className="text-red-600 text-sm mt-1"
                                                    />
                                                    {key === "plateNo" && (isFetchingVin || isDecodingVin) && (
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                                            {isFetchingVin ? "Fetching VIN..." : "Decoding VIN..."}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }



                                    }

                                    return (
                                        <div key={key}>
                                            <label className="block font-medium mb-1">
                                                {field.label}{" "}
                                                {field.required && <span className="text-red-500">*</span>}
                                            </label>

                                            {
                                                type === "select" ? (
                                                    <Field name={key}>
                                                        {(data: any) => {
                                                            const currentValue = data.form.values[data.field.name];
                                                            const currentLabel = field.options?.find((opt: any) => (opt?.value ?? opt) === currentValue)?.label ?? field.placeholder ?? "Select option";
                                                            return (
                                                                <Popover open={!!openDropdowns[key]} onOpenChange={(open) => setOpenDropdowns((prev) => ({ ...prev, [key]: open }))}>
                                                                    <PopoverTrigger asChild>
                                                                        <Button variant="outline" className="w-full justify-between">
                                                                            <span className="truncate">{currentLabel}</span>
                                                                        </Button>
                                                                    </PopoverTrigger>
                                                                    <PopoverContent className="w-full max-h-60 overflow-y-auto p-0">
                                                                        {field.options?.map((opt: any, idx: number) => (
                                                                            <button
                                                                                key={String(opt?.value ?? opt) + idx}
                                                                                className="w-full text-left px-4 py-2 hover:bg-muted"
                                                                                onClick={() => {
                                                                                    data.form.setFieldValue(key, opt?.value ?? opt);
                                                                                    setOpenDropdowns((prev) => ({ ...prev, [key]: false }));
                                                                                }}
                                                                            >
                                                                                {opt?.label ?? String(opt)}
                                                                            </button>
                                                                        ))}
                                                                    </PopoverContent>
                                                                </Popover>
                                                            );
                                                        }}
                                                    </Field>
                                                ) : (
                                                    <Field
                                                        name={key}
                                                    >
                                                        {({ field: fField, form }: any) => {
                                                            const isSerialNoDisabled =
                                                                key === "serialNo" &&
                                                                (!String(form.values?.plateNo ?? "").trim() || isFetchingVin || isDecodingVin);
                                                            return (
                                                                <Input
                                                                    // Keep native text type for numbers to allow inputMode control
                                                                    type={type}
                                                                    placeholder={field.placeholder || ""}
                                                                    {...fField}
                                                                    disabled={isSerialNoDisabled}
                                                                    onChange={(e) => {
                                                                        fField.onChange(e);
                                                                        if (key === "serialNo") {
                                                                            const currentValue = (e.target.value?.trim?.() || "").toUpperCase();
                                                                            const autoValue = autoFilledVin?.trim?.() || "";
                                                                            setIsManualVin(currentValue !== autoValue);
                                                                            if (currentValue.length === 17) {
                                                                                void populateVehicleFieldsFromVin(currentValue, form);
                                                                            }
                                                                        }
                                                                    }}
                                                                // onKeyDown={(e: any) => {
                                                                //     if (type === "number") {
                                                                //         const allowed = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Delete", ".", "-"];
                                                                //         if (!/\d/.test(e.key) && !allowed.includes(e.key)) {
                                                                //             e.preventDefault();
                                                                //         }
                                                                //     }
                                                                // }}
                                                                />
                                                            );
                                                        }}
                                                    </Field>
                                                )}

                                            <ErrorMessage
                                                name={key}
                                                component="div"
                                                className="text-red-600 text-sm mt-1"
                                            />
                                        </div>
                                    );
                                }
                                )
                            ) : (
                                <p className="text-gray-500 dark:text-gray-400"></p>
                            )}
                            <Button
                                className="w-full mt-0 h-10 position-absolute bottom-4 left-0"
                                type="submit"
                                disabled={isSubmitting}
                                onClick={(e) => {
                                    if (isSubmitting) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                            >
                                {isSubmitting ? "Uploading..." : "Submit"}
                            </Button>

                        </Form>
                    )}
                </Formik>
            </div>

        </div>
    );
};

export default RenderForm;